<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import ConfirmModal from '@/components/modals/ConfirmModal';
    import utilsService from '@/services/core/utils.service';
    import httpService from '@/services/core/http.service';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import FormCheckbox from '@/components/shared/form/FormCheckbox';
    import { DxSelectBox } from 'devextreme-vue/select-box';
    import DataSource from 'devextreme/data/data_source';

    const STATUS = {
        InProgress: 0,
        Completed: 1,
        Failed: 2,
        Undone: 3,
        Queued: 4
    };

    const STATUS_LABELS = {
        [STATUS.InProgress]: 'In Progress',
        [STATUS.Completed]: 'Completed',
        [STATUS.Failed]: 'Failed',
        [STATUS.Undone]: 'Undone',
        [STATUS.Queued]: 'Queued'
    };

    const POLL_INTERVAL_MS = 4000;
    const POLL_MAX_MS = 60 * 60 * 1000;

    export default {
        props: {
            modalData: Object
        },
        components: {
            Modal,
            DxSelectBox,
            ButtonSpinner,
            FormCheckbox
        },
        data() {
            return {
                companyId: 0,
                isLoading: 0,
                companiesLoaded: true,
                batteriesLoaded: true,
                companiesDs: {},
                batteriesDs: {},
                selectedCompany: null,
                selectedBattery: null,
                includeMeasurements: false,
                result: null,
                pollTimer: null,
                pollStartedAt: 0
            };
        },
        computed: {
            statusLabel() {
                if (!this.result) return '';
                return STATUS_LABELS[this.result.status] || `Unknown (${this.result.status})`;
            },
            isActiveClone() {
                if (!this.result) return false;
                return this.result.status === STATUS.Queued || this.result.status === STATUS.InProgress;
            }
        },
        methods: {
            close() {
                this.stopPolling();
                modalService.closeModal();
            },
            clone() {
                if (!(this.selectedBattery || {}).id) {
                    utilsService.showToast('Please select a source facility', 'error');
                    return;
                }

                if (!(this.selectedCompany || {}).id) {
                    utilsService.showToast('Please select the destination company', 'error');
                    return;
                }

                if (+this.selectedCompany.id === this.companyId) {
                    utilsService.showToast('Destination company must be different from the source company', 'error');
                    return;
                }

                const measurementsNote = this.includeMeasurements
                    ? ' including historical measurements (runs in the background; can take several minutes)'
                    : ' without measurements';

                modalService.show(ConfirmModal, {
                    title: 'Clone Facility',
                    msg: `Clone <strong>${this.selectedBattery.name}</strong> into <strong>${this.selectedCompany.name}</strong>${measurementsNote}?`,
                    btnTextOk: 'Clone',
                    onSuccess: () => this.runClone()
                });
            },
            runClone() {
                const url = `facility-clones?sourceBatteryId=${this.selectedBattery.id}&targetCompanyId=${this.selectedCompany.id}&includeMeasurements=${this.includeMeasurements}`;

                this.stopPolling();
                this.isLoading++;
                httpService
                    .post(url)
                    .then(data => {
                        this.result = data;
                        utilsService.showToast(
                            `Clone queued as operation #${data.cloneOperationId}. Tracking progress…`
                        );
                        this.startPolling(data.cloneOperationId);
                    })
                    .finally(() => this.isLoading--);
            },
            startPolling(cloneOperationId) {
                this.pollStartedAt = Date.now();
                this.pollTimer = setInterval(() => this.pollStatus(cloneOperationId), POLL_INTERVAL_MS);
                this.pollStatus(cloneOperationId);
            },
            stopPolling() {
                if (this.pollTimer) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                }
            },
            pollStatus(cloneOperationId) {
                if (Date.now() - this.pollStartedAt > POLL_MAX_MS) {
                    this.stopPolling();
                    utilsService.showToast(
                        `Clone #${cloneOperationId} is still running. Check Clone History for status.`,
                        'warn',
                        10000
                    );
                    return;
                }

                httpService
                    .get(`facility-clones/${cloneOperationId}`, null, { customCatch: true })
                    .then(data => {
                        this.result = {
                            cloneOperationId: data.id,
                            newBatteryId: data.targetBatteryId,
                            status: data.status,
                            skippedDowntimeCount: this.result?.skippedDowntimeCount,
                            skippedCustomFieldCount: this.result?.skippedCustomFieldCount
                        };

                        if (data.status === STATUS.Completed) {
                            this.stopPolling();
                            utilsService.showToast(
                                `Facility cloned. New battery #${data.targetBatteryId} (operation #${data.id})`
                            );
                        } else if (data.status === STATUS.Failed || data.status === STATUS.Undone) {
                            this.stopPolling();
                            utilsService.showToast(
                                `Clone operation #${data.id} ended as ${STATUS_LABELS[data.status] || data.status}`,
                                'error',
                                10000
                            );
                        }
                    })
                    .catch(() => {
                        // Keep polling through transient errors; history remains the source of truth.
                    });
            },
            loadCompanies() {
                this.selectedCompany = null;
                this.companiesLoaded = false;

                httpService
                    .get('companies')
                    .then(data => {
                        this.companiesDs = new DataSource({
                            key: 'id',
                            store: data,
                            paginate: true,
                            pageSize: 10
                        });
                    })
                    .finally(() => (this.companiesLoaded = true));
            },
            loadBatteries() {
                this.selectedBattery = null;
                this.batteriesLoaded = false;

                httpService
                    .get(`companies/${this.companyId}/batteries`)
                    .then(data => {
                        this.batteriesDs = new DataSource({
                            key: 'id',
                            store: data,
                            paginate: true,
                            pageSize: 20
                        });
                    })
                    .finally(() => (this.batteriesLoaded = true));
            }
        },
        mounted() {
            this.companyId = this.modalData.item;
            this.loadCompanies();
            this.loadBatteries();
        },
        beforeUnmount() {
            this.stopPolling();
        }
    };
</script>

<template>
    <Modal sizeClass="modal-lg">
        <form class="gsb-form" @submit.prevent="clone">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">Clone Facility</h3>
                </div>
                <div class="modal-body">
                    <p class="text-muted small mb-3">
                        Duplicates one facility into another company via GreaseBookApi (assets, optional measurements,
                        undoable operation). Work runs in the background so large measurement histories do not time out.
                        Collection-point assignations and attachments are not cloned. Use
                        <strong>Clone History</strong> on the Companies toolbar to review or undo past clones.
                    </p>

                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label" for="cloneSourceBattery">Source facility</label>
                        <div class="col-sm">
                            <DxSelectBox
                                id="cloneSourceBattery"
                                v-model="selectedBattery"
                                :data-source="batteriesDs"
                                display-expr="name"
                                :search-enabled="true"
                                search-mode="contains"
                                search-expr="name"
                                :search-timeout="200"
                                drop-down-button-template="batteryLoadIcon"
                            >
                                <template #batteryLoadIcon="{}">
                                    <div>
                                        <span
                                            v-show="!batteriesLoaded"
                                            class="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        ></span>
                                    </div>
                                </template>
                            </DxSelectBox>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label" for="cloneDestCompany">Destination company</label>
                        <div class="col-sm">
                            <DxSelectBox
                                id="cloneDestCompany"
                                v-model="selectedCompany"
                                :data-source="companiesDs"
                                display-expr="name"
                                :search-enabled="true"
                                search-mode="contains"
                                search-expr="name"
                                :search-timeout="200"
                                drop-down-button-template="companyLoadIcon"
                            >
                                <template #companyLoadIcon="{}">
                                    <div>
                                        <span
                                            v-show="!companiesLoaded"
                                            class="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        ></span>
                                    </div>
                                </template>
                            </DxSelectBox>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-3"></div>
                        <div class="col-sm">
                            <FormCheckbox id="cloneIncludeMeasurements" v-model="includeMeasurements">
                                Include historical measurements
                            </FormCheckbox>
                            <div class="form-text">
                                When enabled, measurement history is copied and production is recalculated in the
                                background (can take a long time for years of data).
                            </div>
                        </div>
                    </div>

                    <div v-if="result" class="alert mb-0" :class="isActiveClone ? 'alert-info' : 'alert-success'">
                        <div>
                            <strong>Clone operation #{{ result.cloneOperationId }}</strong>
                            <span class="badge bg-secondary ms-2">{{ statusLabel }}</span>
                            <span v-if="isActiveClone" class="spinner-border spinner-border-sm ms-2" role="status"></span>
                        </div>
                        <div v-if="result.newBatteryId">New battery id: {{ result.newBatteryId }}</div>
                        <div v-if="isActiveClone" class="small mt-1">
                            Progress updates every few seconds. You can close this dialog and use Clone History.
                        </div>
                        <div v-if="result.skippedDowntimeCount">Skipped downtime rows: {{ result.skippedDowntimeCount }}</div>
                        <div v-if="result.skippedCustomFieldCount">
                            Skipped custom fields: {{ result.skippedCustomFieldCount }}
                        </div>
                    </div>
                </div>
                <div class="modal-footer d-flex justify-content-between">
                    <button type="button" class="btn" @click="close">Close</button>
                    <ButtonSpinner :isLoading="isLoading || isActiveClone" text="Clone" />
                </div>
            </div>
        </form>
    </Modal>
</template>
